import { Button, createStyles, FormControl, FormGroup, makeStyles, TextField } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { FieldProps, IChangeEvent, ISubmitEvent } from '@rjsf/core';
import Form from '@rjsf/material-ui';
import { useUpdateNode } from 'apollo/mutations/Nodes';
import { useGetNode } from 'apollo/queries/Nodes';
import { useGetModelSchemaForModel, useGetUiSchema } from 'apollo/queries/Schema';
import { GalleryContext } from 'context/gallery/GalleryContext';
import { ModelContext } from 'context/model/ModelContext';
import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { getSchemaByNode } from 'utils/Schema';
import { JSONSchema7Object } from "json-schema";
import { ErrorContext } from 'context/error/ErrorContext';


import{AjvError} from'@rjsf/core'


interface IAttributedEditor {
    id: number;
    parentId: string | undefined;
}

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            height: '100%',
            maxHeight: 'calc(100vh - 280px)',
            overflow: 'scroll',
        },
    }),
);

const AttributesEditor = ({ id, parentId }: IAttributedEditor) => {
    const { data: { node } = {}, loading } = useGetNode({ variables: { id: id  }});
    const { data: { uiSchema } = {}, loading: uiSchemaLoading } = useGetUiSchema();
    const { state: modelState, setState: setModelState } = useContext(ModelContext);
    const { data: { modelSchema } = { }} = useGetModelSchemaForModel({ variables: {model:modelState.currentModelCode }});
    const [updateNode] = useUpdateNode({ variables: { id: 0, node_attributes: ''  }});
    const modelSchemaObject = modelSchema && JSON.parse(modelSchema);
    const body = node && JSON.parse(node.node_attributes)
    const nodeSchema = modelSchemaObject && node && getSchemaByNode(modelSchemaObject, body.type, false, node.parent_id === null);
    const [formData, setFormData] = useState<unknown>({});
    const [uniqueKey, setUniqueKey] = useState<number>(0);
    const classes = useStyles();
    const { setErrorState } = useContext(ErrorContext);


    const IconFieldTemplate = (props: FieldProps) => {
        const { state, setGalleryState } = useContext(GalleryContext);
        const [files, setFiles] = useState({ ...props.formData });
        const [selectedInput, setSelectedInput] = useState<string>();
        const idSchemaArray: string[] = [];

        const formatFileName = (file: string) => (file.includes('//') ? file.replace('//', '') : file.replace('/', ''));

        // Fetch all the different icon states that are available
        Object.keys(props.idSchema).forEach((key: string) => {
            if (key !== '$id') {
                idSchemaArray.push(key);
            }
        });

        useEffect(() => {
            if (!state.openGallery && state.selectedFile && selectedInput) {
                setFiles({
                    ...files,
                    [selectedInput]: formatFileName(state.selectedFile),
                });
            }
        }, [state]);

        useEffect(() => {
            // Return the changed filenames to the json schema
            if (files) {
                return props.onChange(files);
            }
        }, [files, selectedInput]);

        const handleChange = (name: string, event: ChangeEvent<HTMLInputElement>) => {
            return setFiles({
                ...files,
                [name]: formatFileName(event.target.value),
            });
        };

        return (
            <React.Fragment>
                <h3>{props?.name}</h3>
                <FormControl fullWidth={true}>
                    {idSchemaArray?.map((file: string) => {
                        const properties: JSONSchema7Object = props.schema.properties as JSONSchema7Object;
                        const property: JSONSchema7Object = properties[file] as JSONSchema7Object;
                        return <FormGroup
                            style={{ display: 'flex', flexDirection: 'row', width: '100%', margin: '10px 0px' }}
                            key={ file + Math.random() }
                        >
                            <TextField
                                type='text'
                                disabled={ true }
                                style={{ width: 350, margin: 0 }}
                                label={ property.description || file }
                                value={ files[file] }
                                onChange={ (event: ChangeEvent<HTMLInputElement>) => handleChange(file, event) }
                            />

                            <Button
                                variant='contained'
                                color='primary'
                                style={{ marginLeft: 25 }}
                                onClick={ () => {
                                    setSelectedInput(file);
                                    return setGalleryState({...state, openGallery: true});
                                 }}
                            >
                                Select file
                            </Button>
                            <Button
                                variant='outlined'
                                color='secondary'
                                style={{ marginLeft: 25 }}
                                onClick={ () => {
                                    setFiles({...files, [file]: ''});
                                 }}
                            >
                                Remove
                            </Button>
                        </FormGroup>
                    })}
                </FormControl>
            </React.Fragment>
        );
    };
    let uiSchemaObject = uiSchema && JSON.parse(uiSchema) || {};
    if (uiSchemaObject) {
        uiSchemaObject.icon = {
            "ui:field": IconFieldTemplate
        };

        const defaults = {
            "version": { "ui:readonly": true }
        };
        uiSchemaObject = {...uiSchemaObject, ...defaults};
    }
    useEffect(() => {
        // To prevent form caching we need this to update the forms correctly.
        setUniqueKey(Date.now());
        setFormData(node && JSON.parse(node.node_attributes));

        return () => {
            setFormData({});
        };
    }, [node]);

    const OnChangeHandler = (e: IChangeEvent<unknown>) => {
        setFormData(e.formData);
    };

    const OnSaveHandler = (e: ISubmitEvent<unknown>) => {
        const variables: any = {
            id,
            node_attributes: JSON.stringify(e.formData),
        }
        if (typeof parentId == "number") {
            variables.parent_id = parentId;
        }
        updateNode({
            variables: variables
        });

        setModelState({
            ...modelState,
            updatedModel: true,
            hasChanges: true,
            hasEdited: true,
            showEditedMessage: true,
        });

        // Reset the edited message but keep al the changes, necessary for export button state
        setTimeout(() => {
            return setModelState({
                ...modelState,
                updatedModel: true,
                hasChanges: true,
                hasEdited: true,
                showEditedMessage: false,
            });
        }, 1000);
    };

    const TransformErrors = (errors: AjvError[]) => {
        return errors?.map((error: AjvError) => {
            console.warn(error)
            return error
        })
    }

    const OnErrorHandler = (errors:JSONSchema7Object[]) => {
        setErrorState({ hasError: true, message: errors[0].stack as string })
    };

    return loading || uiSchemaLoading ? (
        <CircularProgress />
    ) : (
        <div className={classes.root}>
            {nodeSchema && node?.node_attributes && (
                <>
                    <h2>{JSON.parse(node?.node_attributes)?.label}</h2>
                    <Form
                        showErrorList={false}
                        key={uniqueKey}
                        schema={nodeSchema}
                        uiSchema={uiSchemaObject}
                        formData={formData}
                        onSubmit={(e) => OnSaveHandler(e)}
                        onChange={(e) => OnChangeHandler(e)}
                        onError={(e) => OnErrorHandler(e)}
                        transformErrors={TransformErrors}
                    />
                </>
            )}
        </div>
    );
};

export default AttributesEditor;
